<template>
    <app-layout>
        <base-header :shadow="false" border border-bg-from="from-yellow-500" border-bg-to="to-orange-500"
                     :back-url="route('stores.budgets.select')">
            <div class="flex items-center justify-between w-full">
                <div>
                    <h4 class="text-xl font-medium">
                        {{ $t('general.budgets') }}
                    </h4>

                    <span>
                        {{ store.name }}
                    </span>
                </div>

                <div class="flex items-center">
                    <default-button icon="plus" size="sm"
                                    @click.native="createBudget">
                        {{ $t('budget.create') }}
                    </default-button>
                </div>
            </div>
        </base-header>

        <table-base class="text-sm" :busy="isLoading">
            <template #head>
                <table-head sort="name" :sort-query="sortQuery">{{ $t('general.name') }}</table-head>
                <table-head sort="year" :sort-query="sortQuery">{{ $t('date.year') }}</table-head>
                <table-head sort="active" :sort-query="sortQuery">{{ $t('general.active') }}</table-head>
                <table-head sort="status" :sort-query="sortQuery">{{ $t('general.status') }}</table-head>
                <table-head></table-head>
            </template>
            <template #body>
                <table-row v-for="budget in budgets.data" :key="budget.id">
                    <table-cell>
                        {{ budget.name }}
                    </table-cell>
                    <table-cell>
                        {{ budget.year }}
                    </table-cell>
                    <table-cell>
                        <icon v-if="budget.active" :size="6" name="check-circle"
                              class-icon="text-green-600"
                        ></icon>
                        <icon v-else :size="6" name="x-circle" class-icon="text-red-600"></icon>
                    </table-cell>
                    <table-cell>
                        <status-badge :budget="budget"/>
                    </table-cell>
                    <table-cell>
                        <action-bar>
                            <lock-button
                                v-if="auth.user.is_admin && !budget.is_in_review"
                                :is-locked="budget.is_locked"
                                :loading="isLocking.includes(budget.id)"
                                @click.native="toggleLock(budget)"
                            />

                            <span
                                v-else
                                :class="[budget.is_locked || budget.is_in_review ? 'text-red-700 dark:text-red-500' : 'text-green-700 dark:text-green-500']"
                            >
                                <icon :name="budget.is_locked ? 'lock-closed' : 'lock-open'"/>
                            </span>

                            <default-button icon="clipboard-list" size="xs" color="blue"
                                            :href="route('stores.budgets.edit', [store.id, budget.id])"
                            >
                                {{ $t('general.entries') }}
                            </default-button>
                            <default-button icon="pencil" size="xs" @click.native="editBudget(budget)"/>
                            <delete-button :loading="isDeleting.includes(budget.id)"
                                           :modal-title="$t('budget.delete')"
                                           @delete="deleteBudget(budget)"
                            >
                                <template #modal-body>
                                    {{ $t('budget.delete_confirm') }}
                                </template>
                            </delete-button>
                        </action-bar>
                    </table-cell>
                </table-row>
            </template>
        </table-base>
        <table-pagination v-if="budgets && budgets.meta" :meta="budgets.meta.pagination" :links="budgets.links"
                          @paginate="loadPage"/>

        <element-modal :open="form.isOpen" @close="resetForm" :title="form.store ? form.store.name : null">
            <template #body>
                <budget-form :store="store" v-model="form.budget"></budget-form>
            </template>
            <template #footer>
                <default-button class="ml-2" icon="save" size="sm" @click.native="saveBudget" :loading="form.isLoading">
                    {{ form.mode === 'create' ? $t('form.create') : $t('form.submit') }}
                </default-button>
            </template>
        </element-modal>
    </app-layout>
</template>

<script>
import Store from '@/Models/Store';
import Budget from '@/Models/Budget';
import BudgetForm from '@/Pages/Stores/Budgets/Components/Form';
import StatusBadge from '@/Pages/Stores/Budgets/Components/StatusBadge';
import LockButton from '@/Pages/Stores/Components/LockButton';

export default {
    components: {BudgetForm, StatusBadge, LockButton},
    props: {
        user: Object,
        store: Store,
    },

    data: function() {
        return {
            budgets: {},
            isLoading: false,
            isDeleting: [],
            isLocking: [],
            page: 1,
            sortQuery: ['-year', '-active'],
            year: null,

            form: {
                isOpen: false,
                isLoading: false,
                mode: 'create',
                budget: null,
            },
        };
    },

    metaInfo() {
        return {
            title: this.$t('general.budgets'),
        };
    },

    async created() {
        this.stores = await Store.$all();

        const urlParams = this.$helper.url().parameters();

        if (urlParams.has('page')) {
            this.page = urlParams.get('page') ?? 1;
        }

        if (urlParams.has('sort')) {
            this.sortQuery = urlParams.get('sort').split(',');
        }

        if (urlParams.has('year')) {
            this.year = parseInt(urlParams.get('year'));
        }

        await this.loadPage(parseInt(this.page));
    },

    methods: {
        toggleLock(budget) {
            this.isLocking.push(budget.id);

            axios.post(route('api.budgets.lock', [budget.id])).then(() => {
                this.applyQuery();
                this.isLocking.splice(this.isLocking.findIndex(id => id === budget.id), 1);
            });
        },

        createBudget() {
            this.form.budget = new Budget({name: null, year: null}).for(this.store);
            this.form.mode = 'create';
            this.form.isOpen = true;
        },

        editBudget(budget) {
            this.form.budget = budget;
            this.form.mode = 'edit';
            this.form.isOpen = true;
        },

        async saveBudget() {
            this.form.isLoading = true;
            await this.form.budget.save();
            await this.applyQuery();
            this.resetForm();
        },

        async deleteBudget(budget) {
            this.isDeleting.push(budget.id);
            await budget.delete();
            this.isDeleting = this.isDeleting.filter(item => item !== budget.id);
            await this.applyQuery();
        },

        resetForm() {
            this.form.mode = 'create';
            this.form.isOpen = false;
            this.form.isLoading = false;
            this.form.budget = null;
        },

        async loadPage(page) {
            this.page = page;
            await this.applyQuery();
        },

        async applyQuery() {
            this.isLoading = true;

            let query = Budget.pageNumber(this.page).where('store_id', this.store.id).limit(20).orderBy(this.sortQuery);

            if (this.year) {
                query = query.where('year', this.year);
            }

            this.budgets = await query.all();
            this.isLoading = false;
        },
    },

    watch: {
        sortQuery() {
            this.applyQuery();
        },
    },
};
</script>
